import React, { useEffect, useState, useRef } from "react";
import { motion, useInView } from "framer-motion";

function StatCounter({ target, suffix = "", duration = 1.5 }) {
  const [count, setCount] = useState(0);
  const elementRef = useRef(null);
  const isInView = useInView(elementRef, { once: true, margin: "-100px" });

  useEffect(() => {
    if (!isInView) return;

    let start = 0;
    const end = parseInt(target.replace(/,/g, ""), 10);
    if (isNaN(end)) {
      // For decimal numbers like 99.9
      const endFloat = parseFloat(target);
      if (isNaN(endFloat)) {
        setCount(target);
        return;
      }
      const totalSteps = 60;
      const stepTime = (duration * 1000) / totalSteps;
      let currentStep = 0;

      const timer = setInterval(() => {
        currentStep++;
        const progress = currentStep / totalSteps;
        const currentCount = progress * endFloat;
        setCount(parseFloat(currentCount.toFixed(1)));

        if (currentStep >= totalSteps) {
          clearInterval(timer);
          setCount(endFloat);
        }
      }, stepTime);

      return () => clearInterval(timer);
    }

    const totalSteps = 60;
    const stepTime = (duration * 1000) / totalSteps;
    let currentStep = 0;

    const timer = setInterval(() => {
      currentStep++;
      const progress = currentStep / totalSteps;
      const currentCount = Math.floor(progress * end);
      setCount(currentCount.toLocaleString());

      if (currentStep >= totalSteps) {
        clearInterval(timer);
        setCount(end.toLocaleString());
      }
    }, stepTime);

    return () => clearInterval(timer);
  }, [isInView, target, duration]);

  return (
    <span ref={elementRef}>
      {count}
      {suffix}
    </span>
  );
}

export default function SaasTrust() {
  const stats = [
    { label: "Active Institutes", value: "100", suffix: "+" },
    { label: "Global Students Enrolled", value: "50000", suffix: "+" },
    { label: "AI Exams Conducted", value: "1000000", suffix: "+" },
    { label: "Infrastructure Uptime", value: "99.9", suffix: "%" },
  ];

  return (
    <section className="py-16 bg-slate-900 text-white relative overflow-hidden">
      {/* Background radial highlight */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(59,130,246,0.15),transparent_60%)]" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-12 items-center text-center">
          {stats.map((stat, i) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              className="space-y-2 relative"
            >
              {/* Stat counter number */}
              <p className="text-4xl sm:text-5xl font-black bg-gradient-to-r from-violet-400 via-blue-400 to-cyan-400 bg-clip-text text-transparent tracking-tight">
                <StatCounter target={stat.value} suffix={stat.suffix} />
              </p>
              
              {/* Stat label */}
              <p className="text-xs sm:text-sm font-bold text-slate-400 uppercase tracking-widest">
                {stat.label}
              </p>

              {/* Decorative separator for large screens */}
              {i < 3 && (
                <div className="hidden lg:block absolute top-1/2 right-0 h-12 w-px bg-gradient-to-b from-transparent via-slate-700 to-transparent -translate-y-1/2" />
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
